import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { UserCog, CheckCircle, XCircle, Clock, Lock, Search, RefreshCw, KeyRound, ShieldCheck } from 'lucide-react';
import { USER_ROLE_LABELS, type UserRole } from 'shared';
import { api } from '@/lib/api';
import { isSuperAdmin, staffActions } from '@/lib/staffPermissions';
import { useAuthStore } from '@/stores/auth';
import EditStaffModal from '@/components/EditStaffModal';
import ResetLinkDialog from '@/components/ResetLinkDialog';

// ─── Types ─────────────────────────────────────────────────────────────────

interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
  status: string;
  createdAt: string;
  lockedAt?: string | null;
}

interface UsersResponse {
  users: AdminUser[];
  total: number;
  limit: number;
  offset: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string; Icon: React.ElementType }> = {
    active: {
      label: 'Activo',
      className: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
      Icon: CheckCircle,
    },
    pending: {
      label: 'Pendente',
      className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
      Icon: Clock,
    },
    inactive: {
      label: 'Inactivo',
      className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
      Icon: XCircle,
    },
    locked: {
      label: 'Bloqueado',
      className: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
      Icon: Lock,
    },
  };

  const config = map[status] ?? {
    label: status,
    className: 'bg-muted text-muted-foreground',
    Icon: Clock,
  };
  const { label, className, Icon } = config;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const admin = 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
  const roleMap: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
    admin,
    coordinator: admin,
  };
  const cls = roleMap[role] ?? 'bg-muted text-muted-foreground';
  const label = USER_ROLE_LABELS[role as UserRole] ?? role;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

// ─── Password Generator ────────────────────────────────────────────────────

function generatePassword(length = 14): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%&*?';
  const all = upper + lower + digits + special;
  const random = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
  // Guarantee at least one of each required category
  const required = [random(upper), random(lower), random(digits), random(special)];
  const rest = Array.from({ length: length - required.length }, () => random(all));
  return [...required, ...rest].sort(() => Math.random() - 0.5).join('');
}

// ─── Change Password Modal ─────────────────────────────────────────────────

interface ChangePasswordModalProps {
  isOpen: boolean;
  userName: string;
  loading: boolean;
  onSave: (password: string) => void;
  onClose: () => void;
}

function ChangePasswordModal({ isOpen, userName, loading, onSave, onClose }: ChangePasswordModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Generate a fresh suggestion each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setPassword(generatePassword());
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (password.trim().length < 8) {
      setError('A password deve ter pelo menos 8 caracteres.');
      return;
    }
    setError('');
    onSave(password.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-primary" />
          <h2 className="text-base font-semibold text-foreground">Alterar Password</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Nova password para <span className="font-medium text-foreground">"{userName}"</span>.
          Podes usar a sugestão ou escrever uma nova.
        </p>

        <div className="space-y-1 mb-1">
          <label className="text-xs font-medium text-muted-foreground">Nova password</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              disabled={loading}
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50 font-mono"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => { setPassword(generatePassword()); setError(''); }}
              disabled={loading}
              title="Gerar nova sugestão"
              className="px-2 py-2 rounded-lg border border-border bg-muted hover:bg-accent text-muted-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-sm font-medium text-white transition-colors disabled:opacity-50"
          >
            {loading ? 'A gravar...' : 'Gravar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirmation Modal ────────────────────────────────────────────────────

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'primary' | 'danger';
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function ConfirmModal({ isOpen, title, message, confirmLabel, variant, loading, onConfirm, onClose }: ConfirmModalProps) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <h2 className="text-base font-semibold text-foreground mb-2">{title}</h2>
        <p className="text-sm text-muted-foreground mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${
              variant === 'danger'
                ? 'bg-destructive hover:bg-destructive/90'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {loading ? 'A processar...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirmed actions ─────────────────────────────────────────────────────

type ConfirmAction = 'activate' | 'revoke' | 'remove';

const CONFIRM_COPY: Record<
  ConfirmAction,
  {
    title: string;
    confirmLabel: string;
    variant: 'primary' | 'danger';
    message: (name: string) => string;
    done: (name: string) => string;
  }
> = {
  activate: {
    title: 'Activar utilizador',
    confirmLabel: 'Activar',
    variant: 'primary',
    message: (name) =>
      `Tens a certeza que queres activar o acesso de "${name}"? O utilizador poderá fazer login imediatamente.`,
    done: (name) => `Utilizador "${name}" activado com sucesso.`,
  },
  revoke: {
    title: 'Revogar acesso',
    confirmLabel: 'Revogar',
    variant: 'danger',
    message: (name) =>
      `Tens a certeza que queres revogar o acesso de "${name}"? O utilizador não conseguirá fazer login enquanto estiver inactivo.`,
    done: (name) => `Acesso de "${name}" revogado.`,
  },
  remove: {
    title: 'Remover conta',
    confirmLabel: 'Remover',
    variant: 'danger',
    message: (name) =>
      `Tens a certeza que queres remover a conta de "${name}"? A conta deixa de existir no backoffice. O que essa pessoa criou fica como está.`,
    done: (name) => `Conta de "${name}" removida.`,
  },
};

// ─── Main page ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export default function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUser = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(currentUser);

  // Confirm modal state
  const [modal, setModal] = useState<{
    open: boolean;
    userId: number;
    action: ConfirmAction;
    userName: string;
  }>({ open: false, userId: 0, action: 'activate', userName: '' });

  // Edit modal state
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resetLinkFor, setResetLinkFor] = useState<AdminUser | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Change password modal state
  const [pwModal, setPwModal] = useState<{
    open: boolean;
    userId: number;
    userName: string;
  }>({ open: false, userId: 0, userName: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const fetchUsers = useCallback(async (currentOffset: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<UsersResponse>(
        `/api/users?limit=${PAGE_SIZE}&offset=${currentOffset}`
      );
      setUsers(data.users ?? []);
      setTotal(data.total);
    } catch {
      setError('Erro ao carregar utilizadores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(offset);
  }, [fetchUsers, offset]);

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const openModal = (userId: number, userName: string, action: ConfirmAction) => {
    setModal({ open: true, userId, action, userName });
  };

  const openPwModal = (userId: number, userName: string) => {
    setPwModal({ open: true, userId, userName });
  };

  const handleChangePassword = async (newPassword: string) => {
    setPwLoading(true);
    try {
      await api.post(`/api/users/${pwModal.userId}/change-password`, { newPassword });
      setToast({
        message: `Password de "${pwModal.userName}" alterada com sucesso.`,
        type: 'success',
      });
      setPwModal(m => ({ ...m, open: false }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar a password.';
      setToast({ message, type: 'error' });
    } finally {
      setPwLoading(false);
    }
  };

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      if (modal.action === 'remove') {
        await api.delete(`/api/users/${modal.userId}`);
      } else {
        await api.post(`/api/users/${modal.userId}/${modal.action}`, {});
      }
      setToast({ message: CONFIRM_COPY[modal.action].done(modal.userName), type: 'success' });
      setModal(m => ({ ...m, open: false }));
      fetchUsers(offset);
    } catch {
      setToast({ message: 'Ocorreu um erro. Tenta novamente.', type: 'error' });
      setModal(m => ({ ...m, open: false }));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async (data: { name: string; email: string }) => {
    if (!editing) return;
    setEditLoading(true);
    try {
      await api.put(`/api/users/${editing.id}`, data);
      setToast({ message: `Conta de "${data.name}" actualizada.`, type: 'success' });
      setEditing(null);
      fetchUsers(offset);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao actualizar a conta.';
      setToast({ message, type: 'error' });
    } finally {
      setEditLoading(false);
    }
  };

  // Client-side search filter (name / email)
  const filtered = search.trim()
    ? users.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <>
      <Helmet>
        <title>Administradores | DIGGER</title>
      </Helmet>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-20 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white transition-all ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-destructive'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <UserCog className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Administradores</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {superAdmin
                  ? 'Aprova, edita, revoga ou remove as contas da equipa.'
                  : 'As contas da equipa com acesso ao backoffice.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => fetchUsers(offset)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {!superAdmin && (
          <div
            role="note"
            className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
          >
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
            <p>
              Todos os administradores podem fazer tudo no backoffice, menos gerir as contas uns dos
              outros: isso cabe ao Super Admin. Aqui podes alterar a tua própria password.
            </p>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {['pending', 'active', 'inactive', 'locked'].map(s => {
            const count = users.filter(u => u.status === s).length;
            return (
              <div key={s} className="card p-4 flex items-center gap-3">
                <StatusBadge status={s} />
                <span className="text-2xl font-bold text-foreground tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>

        {/* Table card */}
        <div className="card">
          {/* Search bar */}
          <div className="px-6 py-4 border-b border-border flex items-center gap-3">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Pesquisar por nome ou email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Limpar
              </button>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="px-6 py-12 text-center text-muted-foreground text-sm">A carregar...</div>
          ) : error ? (
            <div className="px-6 py-12 text-center text-destructive text-sm">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-muted-foreground text-sm">
              Nenhum utilizador encontrado.
            </div>
          ) : (
            <>
              {/* On a narrow screen the list scrolls sideways rather than squeeze. */}
              <div className="overflow-x-auto">
              <div className="min-w-[760px]">
              {/* Header */}
              <div className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr] px-6 py-2 bg-muted/30 text-xs font-medium text-muted-foreground">
                <span>Nome</span>
                <span>Email</span>
                <span>Perfil</span>
                <span>Estado</span>
                <span className="text-right">Acções</span>
              </div>

              {/* Rows */}
              <div className="divide-y divide-border">
                {filtered.map(u => {
                  const can = staffActions(currentUser, u);
                  return (
                    <div
                      key={u.id}
                      className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr] px-6 py-3 items-center hover:bg-accent/30 transition-colors"
                    >
                      {/* Name */}
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground">{u.name}</div>
                          <div className="text-xs text-muted-foreground">
                            #{u.id} · {new Date(u.createdAt).toLocaleDateString('pt-PT')}
                          </div>
                        </div>
                      </div>

                      {/* Email */}
                      <span className="text-sm text-muted-foreground truncate">{u.email}</span>

                      {/* Role */}
                      <RoleBadge role={u.role} />

                      {/* Status */}
                      <StatusBadge status={u.status} />

                      {/* Actions */}
                      <div className="flex justify-end gap-2 flex-wrap">
                        {can.edit && (
                          <button
                            onClick={() => setEditing(u)}
                            aria-label={`Editar ${u.name}`}
                            className="px-3 py-1.5 rounded-md bg-muted text-foreground hover:bg-accent text-xs font-semibold transition-colors"
                          >
                            Editar
                          </button>
                        )}
                        {can.changePassword && (
                          <button
                            onClick={() => openPwModal(u.id, u.name)}
                            aria-label={`Alterar password de ${u.name}`}
                            className="px-3 py-1.5 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-xs font-semibold transition-colors"
                          >
                            Alterar Pass
                          </button>
                        )}
                        {can.resetLink && (
                          <button
                            onClick={() => setResetLinkFor(u)}
                            aria-label={`Gerar ligação de recuperação para ${u.name}`}
                            className="px-3 py-1.5 rounded-md bg-muted text-foreground hover:bg-accent text-xs font-semibold transition-colors"
                          >
                            Ligação de recuperação
                          </button>
                        )}
                        {can.activate && (
                          <button
                            onClick={() => openModal(u.id, u.name, 'activate')}
                            aria-label={`Activar ${u.name}`}
                            className="px-3 py-1.5 rounded-md bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/40 text-xs font-semibold transition-colors"
                          >
                            Activar
                          </button>
                        )}
                        {can.revoke && (
                          <button
                            onClick={() => openModal(u.id, u.name, 'revoke')}
                            aria-label={`Revogar ${u.name}`}
                            className="px-3 py-1.5 rounded-md bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 text-xs font-semibold transition-colors"
                          >
                            Revogar
                          </button>
                        )}
                        {can.remove && (
                          <button
                            onClick={() => openModal(u.id, u.name, 'remove')}
                            aria-label={`Remover ${u.name}`}
                            className="px-3 py-1.5 rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 text-xs font-semibold transition-colors"
                          >
                            Remover
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-border flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    Página {currentPage} de {totalPages} ({total} utilizadores)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      disabled={offset === 0}
                      className="px-3 py-1 rounded border border-border hover:bg-accent transition-colors disabled:opacity-40"
                    >
                      ← Anterior
                    </button>
                    <button
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                      disabled={offset + PAGE_SIZE >= total}
                      className="px-3 py-1 rounded border border-border hover:bg-accent transition-colors disabled:opacity-40"
                    >
                      Seguinte →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Change password modal */}
      <ChangePasswordModal
        isOpen={pwModal.open}
        userName={pwModal.userName}
        loading={pwLoading}
        onSave={handleChangePassword}
        onClose={() => setPwModal(m => ({ ...m, open: false }))}
      />

      {/* Edit modal */}
      {editing && (
        <EditStaffModal
          key={editing.id}
          name={editing.name}
          email={editing.email}
          loading={editLoading}
          onSave={handleEdit}
          onClose={() => setEditing(null)}
        />
      )}

      {resetLinkFor && (
        <ResetLinkDialog
          kind="staff"
          id={resetLinkFor.id}
          name={resetLinkFor.name}
          onClose={() => setResetLinkFor(null)}
        />
      )}

      {/* Confirmation modal */}
      <ConfirmModal
        isOpen={modal.open}
        title={CONFIRM_COPY[modal.action].title}
        message={CONFIRM_COPY[modal.action].message(modal.userName)}
        confirmLabel={CONFIRM_COPY[modal.action].confirmLabel}
        variant={CONFIRM_COPY[modal.action].variant}
        loading={actionLoading}
        onConfirm={handleConfirm}
        onClose={() => setModal(m => ({ ...m, open: false }))}
      />
    </>
  );
}
